import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  fetchArtifactContent,
  fetchArtifactsManifest,
} from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage } from "../common/ErrorCodeMap";
import { formatAbsolute } from "../common/hooks";
import {
  formatArtifactBytes,
  getArtifactPhaseLabel,
  groupArtifactPhases,
} from "../common/artifactPhases";
import { FILTER_FONT_SIZE } from "../common/filterBarStyles";
import ArtifactContentView from "./ArtifactContentView";

/**
 * Lists artifact phases (GET …/artifacts) and loads JSON content per phase
 * (GET …/artifacts/{phase}). Phases are grouped into Pre-/Post-Remediation;
 * selecting a group loads every phase it contains.
 */
const ArtifactsPanel = ({ requestId, active }) => {
  const dispatch = useDispatch();
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const manifestSlice = useSelector((s) =>
    requestId != null ? s.remediation.artifactsManifestById[requestId] : null
  );
  const contentByKey = useSelector((s) => s.remediation.artifactContentByKey);

  const groups = useMemo(
    () => groupArtifactPhases(manifestSlice?.data?.phases || []),
    [manifestSlice?.data?.phases]
  );

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  // Stable dependency for the phases we need to load for the current group.
  const selectedPhasesKey = useMemo(
    () => (selectedGroup?.entries || []).map((e) => e.phase).join("|"),
    [selectedGroup]
  );

  const loadManifest = useCallback(() => {
    if (requestId == null) return;
    dispatch(fetchArtifactsManifest(requestId));
  }, [requestId, dispatch]);

  const loadPhase = useCallback(
    (phase) => {
      if (requestId == null || !phase) return;
      dispatch(fetchArtifactContent({ requestId, phase }));
    },
    [requestId, dispatch]
  );

  const loadSelectedGroupContent = useCallback(() => {
    if (!selectedPhasesKey) return;
    selectedPhasesKey.split("|").forEach(loadPhase);
  }, [selectedPhasesKey, loadPhase]);

  const refreshAll = useCallback(() => {
    loadManifest();
    loadSelectedGroupContent();
  }, [loadManifest, loadSelectedGroupContent]);

  useEffect(() => {
    setSelectedGroupId(null);
  }, [requestId]);

  useEffect(() => {
    if (!active || requestId == null) return;
    loadManifest();
  }, [active, requestId, loadManifest]);

  useEffect(() => {
    if (!active || groups.length === 0) {
      if (groups.length === 0) setSelectedGroupId(null);
      return;
    }
    if (!selectedGroupId || !groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [active, groups, selectedGroupId]);

  useEffect(() => {
    if (!active) return;
    loadSelectedGroupContent();
  }, [active, loadSelectedGroupContent]);

  const items = useMemo(
    () =>
      (selectedGroup?.entries || []).map((entry) => ({
        entry,
        slice:
          requestId != null ? contentByKey[`${requestId}:${entry.phase}`] : null,
      })),
    [selectedGroup, contentByKey, requestId]
  );

  const manifestLoading = manifestSlice?.status === "loading";
  const manifestFailed = manifestSlice?.status === "failed";
  const groupLoading = items.some(({ slice }) => slice?.status === "loading");
  const groupHasContent = items.some(({ slice }) => slice?.data != null);
  // First load of a stage: one spinner instead of one per phase.
  const showGroupSpinner = groupLoading && !groupHasContent;

  if (requestId == null) {
    return (
      <Typography variant="caption" color="text.secondary">
        No task selected.
      </Typography>
    );
  }

  return (
    <Box>
      <StackHeader
        onRefresh={refreshAll}
        refreshing={manifestLoading || groupLoading}
      />

      {manifestFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {friendlyErrorMessage(manifestSlice.error)}
        </Alert>
      )}

      {manifestLoading && !manifestSlice?.data && (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={36} />
          <Skeleton variant="rounded" height={200} />
        </Stack>
      )}

      {!manifestLoading && !manifestFailed && groups.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: FILTER_FONT_SIZE }}>
          No artifacts have been captured for this task yet. Output appears after the preview or
          execution steps write to S3.
        </Typography>
      )}

      {groups.length > 0 && (
        <Box>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.75}
            sx={{ mb: 1.5 }}
            role="tablist"
            aria-label="Artifact stages"
          >
            {groups.map((group) => {
              const isSelected = selectedGroupId === group.id;
              return (
                <Chip
                  key={group.id}
                  role="tab"
                  aria-selected={isSelected}
                  label={group.label}
                  onClick={() => setSelectedGroupId(group.id)}
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "primary" : "default"}
                  sx={{
                    height: "auto",
                    py: 0.75,
                    "& .MuiChip-label": {
                      display: "block",
                      whiteSpace: "normal",
                      fontSize: 12,
                      fontWeight: isSelected ? 600 : 500,
                      lineHeight: 1.3,
                      px: 1.25,
                    },
                  }}
                />
              );
            })}
          </Stack>

          {selectedGroup && showGroupSpinner && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Loading {selectedGroup.label.toLowerCase()} output…
              </Typography>
            </Box>
          )}

          {!showGroupSpinner && (
            <Stack spacing={1.5}>
              {items.map(({ entry, slice }) => (
                <PhaseSection
                  key={entry.phase}
                  entry={entry}
                  slice={slice}
                  showHeader={items.length > 1}
                  onRetry={() => loadPhase(entry.phase)}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

/** One artifact phase inside the selected group. */
const PhaseSection = ({ entry, slice, showHeader, onRetry }) => {
  const loading = slice?.status === "loading";
  const failed = slice?.status === "failed";
  const hasContent = slice?.data != null;
  const isInitialLoad = loading && !hasContent;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        p: 2,
        backgroundColor: "grey.50",
      }}
    >
      {showHeader && (
        <Box sx={{ mb: 1.25 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {getArtifactPhaseLabel(entry.phase)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            {formatArtifactBytes(entry.size_bytes)}
            {entry.captured_at ? ` · Captured ${formatAbsolute(entry.captured_at)}` : ""}
          </Typography>
        </Box>
      )}

      {!showHeader && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.25, fontSize: 11 }}
        >
          {formatArtifactBytes(entry.size_bytes)}
          {entry.captured_at ? ` · Captured ${formatAbsolute(entry.captured_at)}` : ""}
        </Typography>
      )}

      {isInitialLoad && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            Loading…
          </Typography>
        </Box>
      )}

      {failed && (
        <Alert
          severity="warning"
          sx={{ fontSize: 13, mb: hasContent ? 1.5 : 0 }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {friendlyErrorMessage(slice.error)}
        </Alert>
      )}

      {hasContent && <ArtifactContentView phase={entry.phase} data={slice.data} />}

      {slice?.status === "succeeded" && !hasContent && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
          Artifact is empty.
        </Typography>
      )}

      {loading && hasContent && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 1.5,
            pt: 1.5,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Refreshing…
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const StackHeader = ({ onRefresh, refreshing }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5, gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: FILTER_FONT_SIZE, lineHeight: 1.45 }}>
      Task-captured output from the remediation run — what was planned and checked before the
      change, and what happened after.
    </Typography>
    <Button
      size="small"
      startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
      onClick={onRefresh}
      disabled={refreshing}
      sx={{ textTransform: "none", fontSize: 12, flexShrink: 0 }}
    >
      Refresh
    </Button>
  </Box>
);

export default ArtifactsPanel;
